import React, { Component } from 'react';
import { MathUtils } from 'three';
import { useTexture } from '../../../hooks';
import Button from '../button';

import groupBtnTxt from '../../../assets/images/xr/icons/u-story-group-icon.png';
import storyBtnTxt from '../../../assets/images/xr/icons/f-story-icon.png';
import storyBtnTxtWhite from '../../../assets/images/xr/icons/f-story-icon-white.png';
import storyBtnTxtWhiteIconOnly from '../../../assets/images/xr/icons/f-story-icon-white-icon-only.png';
import gameBtnTxt from '../../../assets/images/xr/icons/game-icon-v2.png';
import { CustomObject } from '../../../types/data';

const { degToRad } = MathUtils;

const SEARCH_RADIUS = 0.5;
const ICON_DISTANCE = 1.5; // Z distance from (0,0,0) point

type TRenderStoryList = {
  stories: any[];
  groups: any[];
  onSelectStory: (story: any) => void;
  onSelectGroup: (story: any) => void;
};

const RenderStoryList = ({ stories, groups, onSelectStory, onSelectGroup }: TRenderStoryList) => {
  const { texture: btnGroupTxt } = useTexture(groupBtnTxt);
  const { texture: btnStoryTxt } = useTexture(storyBtnTxt);
  const { texture: btnStoryTxtWhite } = useTexture(storyBtnTxtWhite);
  const { texture: btnStoryTxtWhiteIconOnly } = useTexture(storyBtnTxtWhiteIconOnly);
  const { texture: btnGameTxt } = useTexture(gameBtnTxt);

  return (
    <group name="story-list">
      {groups.map((gr: any) => {
        return (
          <group key={gr.id} rotation={[degToRad(-gr.coords.atv), degToRad(-gr.coords.ath), 0, 'YXZ']}>
            <Button
              position={[0, 0, -ICON_DISTANCE]}
              defaultState={{
                main: {
                  size: 0.11,
                  material: {
                    transparent: true,
                    map: btnGroupTxt,
                  },
                },
              }}
              onClick={() => onSelectGroup(gr)}
            />
          </group>
        );
      })}

      {stories.map((st: any) => {
        return (
          <group key={st.id} rotation={[degToRad(-st.atv), degToRad(-st.ath), 0, 'YXZ']}>
            <Button
              position={[0, 0, -ICON_DISTANCE]}
              defaultState={{
                main: {
                  size: 0.11,
                  material: {
                    transparent: true,
                    color: st.background_color || '#ffffff',
                    map: st.background_color ? btnStoryTxtWhite : btnStoryTxt,
                  },
                },
                icon: {size: 0.11, material: {transparent: true,map: btnStoryTxtWhiteIconOnly}}
              }}
              onClick={() => onSelectStory(st)}
            />
          </group>
        );
      })}
    </group>
  );
};

type TStoryListProps = React.Props<{}> & {
  data: any[];
  activeStory?: CustomObject;
  onSelect: (story: any) => void;
};

class StoryList extends Component<TStoryListProps> {
  state = {
    openedGroupId: null,
  };

  componentDidUpdate() {
    const { activeStory, data = [] } = this.props;
    if (activeStory) {
      const [groups] = this.processData(data);

      const group = groups.find((gr: any) => gr.items.find((st: any) => st.data.id === activeStory.id));

      if (group && group.id !== this.state.openedGroupId) {
        this.handleGroupToggle(group);
      }
    }
  }

  render() {
    const { data = [], onSelect } = this.props;
    const [groups, stories] = this.processData(data);

    return (
      <RenderStoryList
        stories={stories}
        groups={groups}
        onSelectGroup={this.handleGroupToggle}
        onSelectStory={onSelect}
      />
    );
  }

  public closeOpenedGroup = () => this.handleGroupToggle();

  private handleGroupToggle = (gp?: any) => {
    const { openedGroupId } = this.state;

    let groupId: any = null;

    if (gp) {
      groupId = gp.id;
      if (groupId === openedGroupId) {
        groupId = null;
      }
    }

    this.setState(st => ({ ...st, openedGroupId: groupId }));
  };

  private processData = (data: any[]) => {
    const { openedGroupId } = this.state;

    //let [groups, stories] = this.groupByNearest(data);
    let stories = data;
    let groups:any[] = [];

    const activeGroup = groups.find((gr: any) => gr.id === openedGroupId);

    if (activeGroup) {
      stories = [...stories, ...activeGroup.items.map((itm: any) => itm.data)];
      groups = groups.filter((gr: any) => gr.id !== openedGroupId);
    }

    return [groups, stories];
  };

  private sortByNearest = (stories: any[]) => {
    let list = stories.map(st => {
      const phi = ((st.ath + 90) * Math.PI) / 180;
      const theta = -((st.atv + 180) * Math.PI) / 180;
      const r = ICON_DISTANCE;

      const nearest: any[] = [];

      return {
        data: st,
        nearest,
        spherical: {
          phi,
          theta,
          r,
        },
      };
    });

    list = list.map(st => {
      const x = st.spherical.r * Math.sin(st.spherical.phi) * Math.cos(st.spherical.theta);
      const y = st.spherical.r * Math.sin(st.spherical.phi) * Math.sin(st.spherical.theta);
      const z = st.spherical.r * Math.cos(st.spherical.phi);

      const nearest = list.reduce((arr: any[], item) => {
        if (item.data.id !== st.data.id) {
          const iX = item.spherical.r * Math.sin(item.spherical.phi) * Math.cos(item.spherical.theta);
          const iY = item.spherical.r * Math.sin(item.spherical.phi) * Math.sin(item.spherical.theta);
          const iZ = item.spherical.r * Math.cos(item.spherical.phi);

          const dx = x - iX;
          const dy = y - iY;
          const dz = z - iZ;

          if (dx ** 2 + dy ** 2 + dz ** 2 < SEARCH_RADIUS ** 2) {
            return [...arr, item];
          }
        }

        return arr;
      }, []);

      return {
        ...st,
        nearest,
      };
    });

    list.sort((a, b) => {
      if (a.nearest.length > b.nearest.length) {
        return -1;
      } else if (a.nearest.length < b.nearest.length) {
        return 1;
      }

      return 0;
    });

    return list;
  };

  private groupByNearest = (stories: any[], groups: any[] = []): any[] => {
    const [firstInList] = this.sortByNearest(stories);

    if (firstInList && firstInList.nearest.length >= 2) {
      const newGroup = {
        id: groups.length,
        coords: {
          ath: firstInList.data.ath,
          atv: firstInList.data.atv,
        },
        items: [...firstInList.nearest, firstInList],
      };

      groups = [...groups, newGroup];

      stories = stories.filter(st => !newGroup.items.find(itm => itm.data.id === st.id));

      return this.groupByNearest(stories, groups);
    }

    return [groups, stories];
  };
}

export default StoryList;
